import _ from 'lodash'

import { CRUD, CrudModel } from '../../classes'
import { PatientUploadedFile } from '..'
import { AUTHORIZATION_STATUS } from '../auth-status'
import { AvailityServiceReview } from '../availity'
import { PortalModel, PortalQuestion } from '../portal'
import { ProductData, ProductModel } from '../product'
import { AuthorizationData } from './authorization.data'
import { AuthorizationPortalData } from './authorization-portal'

export type LimitationQuantityType = 'visit' | 'hour' | 'day' | 'dosage' | 'FL'

export interface Limitations {
  quantity: number
  quantityType: LimitationQuantityType
}

export enum OutdatedFlags {
  ConflictDates = 'ConflictDates',
  MissingProcedures = 'MissingProcedures',
  WrongFacility = 'WrongFacility',
}

@CRUD({
  base: '/authorizations',
})
export class AuthorizationModel extends CrudModel {
  patientId: string
  appointmentIds: Set<string>
  outdatedAppointmentIds: Set<string>
  ignoredOutdatedIds: Set<string>
  insuranceId?: string
  carrierLookupId?: string
  carrierId?: string
  referrerId?: string
  providerIds?: string[]
  procedureCodes?: string[]
  productIds: string[]
  products: ProductModel[]
  productNames: string[]
  diagnosisCodes?: string[]
  number: string
  startAt?: moment.Moment
  endAt?: moment.Moment
  referenceNumber?: string
  note?: string
  // cannot be typed because the structure depends on the insurance carrier
  fullAuthorizationObject?: AvailityServiceReview.Review
  limitations?: Limitations
  status: AUTHORIZATION_STATUS
  customFields?: Record<string, any>
  attachments?: PatientUploadedFile[]
  nextPullAt?: moment.Moment
  outdatedFlags?: OutdatedFlags[]
  portals?: PortalModel[]
  questions?: PortalQuestion[]
  portalData?: AuthorizationPortalData

  constructor(data: AuthorizationData) {
    super(data, true)

    if (data.startAt) {
      this.startAt = this.transformDate(data.startAt, false)
    }
    if (data.endAt) {
      this.endAt = this.transformDate(data.endAt, false)
    }
    if (data.nextPullAt) {
      this.nextPullAt = this.transformDate(data.nextPullAt, false)
    }

    if (data.portals) {
      this.portals = data.portals.map(portal => new PortalModel(portal))
    }

    this.products = (data.products || []).map((product: ProductData) => new ProductModel(product))
    this.productIds = data.productIds ?? this.products.map(product => product.id)
    this.productNames = this.products.map((product: ProductModel) => product.name)
    this.procedureCodes = data.procedureCodes ?? this.fullAuthorizationObject?.procedures?.map(p => p.code)
    this.products = (data.products || []).map(
      (product: ProductData) => new ProductModel(product),
    )
    this.productIds = data.productIds ?? this.products.map((product) => product.id)
    this.productNames = this.products.map((product: ProductModel) => product.name)
    this.procedureCodes = data.procedureCodes ?? this.fullAuthorizationObject?.procedures?.map((p) => p.code)
    this.appointmentIds = new Set(data.appointmentIds ?? [])
    this.outdatedAppointmentIds = new Set(data.outdatedAppointmentIds ?? [])
    this.ignoredOutdatedIds = new Set(data.ignoredOutdatedIds ?? [])
  }

  get dateFrom(): string | undefined {
    return this.startAt?.format('YYYY-MM-DD')
  }

  get dateTo(): string | undefined {
    return this.endAt?.format('YYYY-MM-DD')
  }

  override toJSON(): any {
    return _.extend(super.toJSON(), {
      patientId: this.patientId,
      appointmentIds: this.appointmentIds
        ? [...this.appointmentIds]
        : undefined,
      insuranceId: this.insuranceId,
      carrierLookupId: this.carrierLookupId,
      carrierId: this.carrierId,
      fullAuthorizationObject: this.fullAuthorizationObject,
      productIds: this.productIds,
      number: this.number,
      startAt: this.startAt,
      endAt: this.endAt,
      referenceNumber: this.referenceNumber,
      status: this.status,
      note: this.note,
      limitations: this.limitations,
    })
  }
}
